import { isPortalRequestPast } from './portal-lead.js';

/**
 * ⭐⭐ Board 2814 M4 (plan DD-11 rows 7, 8 and 14; step 5b) - SETTLING AN OLD
 * UNBOOKED REQUEST BY HAND.
 *
 * ⛔ ONE PREDICATE FOR BOTH SIDES. The settle route re-derives it from a fresh
 * portal read and the crossing before it writes (`server/app.ts`), and the
 * Requests screen derives it to decide which rows offer the two quiet controls
 * (`client/lib/actions.ts` `needsHelenNow`), so the server and the screen cannot
 * disagree about which rows may be settled. `src/shared` is imported by both
 * bundles, so it is typed by shape and depends on neither side's types.
 */

/** The two closed reasons, exactly as `portal_request_settlements_reason_known`
 *  (v46) spells them. A token, never free text: the relation holds no prose. */
export const SETTLE_REASONS = ['booked_another_way', 'not_going_ahead'] as const;
export type SettleReason = (typeof SETTLE_REASONS)[number];

export function isSettleReason(v: unknown): v is SettleReason {
  return typeof v === 'string' && (SETTLE_REASONS as readonly string[]).includes(v);
}

/**
 * DD-11 rows 7 and 8, the only rows a hand settlement may move to Done:
 *   7  Priced or Confirmed, LANDED WITH NO BOOKING, not past;
 *   8  Confirmed, NO CROSSING AT ALL, not past.
 * ⚠️ An open ask is row 9 and a past day is row 4, both decided before these rows
 * in `needsHelenNow`'s first-match order, so neither is settleable. A request
 * with a booking (live or cancelled) is rows 5, 6, 10 or 11 and never is either.
 * `crossing` is undefined when the request never crossed; the caller passes it
 * only from a crossings read it trusts (a failed read is never "no crossing").
 */
export function isSettleableRow(
  r: {
    status: string;
    askState?: string | null | undefined;
    timeMeaning: string | null | undefined;
    pickupAt?: string | null | undefined;
    journeyDate: string | null | undefined;
  },
  crossing: { bookingId: number | null } | undefined,
  now: Date,
): boolean {
  if (r.status !== 'priced' && r.status !== 'confirmed') return false;
  if (r.askState === 'open') return false;
  if (isPortalRequestPast(r, now)) return false;
  if (crossing !== undefined) return crossing.bookingId === null;
  return r.status === 'confirmed';
}
